/**
 * Режим ТЦ (mall-пресет, референс MallExplorer): боковой язычок этажей у края
 * экрана (как SOS) с лифт-панелью выбора + каталог магазинов активного этажа.
 * Каталог: на десктопе — боковая панель, на мобиле — оверлей по пункту ☰
 * в панели выбора.
 * Выбор этажа фильтрует venue-слои (venue-map.setLevel), как floor-switcher.
 */
import { getLang, tr } from '@app/i18n'
import { floorNumberOfLevel, levelTenantCount, localized, type Unit, type Venue } from '@app/rvf'
import { terraCategory } from '@app/tokens'
import { useEffect, useRef, useState } from 'preact/hooks'
import { selectTarget } from '../actions'
import { promoTiers } from '../promo'
import { activeLevelId, langTick, mallDirectoryOpen, targetId, venue, venueMap } from '../state'
import { floorPopPlacement, floorTabNumber, floorTabTop } from './floor-tab'
import { categoryLabel } from './labels'
import { type DockEdge, useEdgeDock } from './use-edge-dock'

function catColor(category: string): string {
  return (terraCategory as Record<string, string>)[category] ?? terraCategory.__default
}

/**
 * Подпись этажа. Номер, а не имя уровня: у Ülemiste уровни названы «0. korrus»,
 * и панель показывала «0/1/2», пока этажерка и «Объём 3D» считали «1/2/3» —
 * одно и то же место называлось по-разному (фидбек владельца 29.07).
 * Собственное имя уровня («Паркинг», «Цоколь») уважается, если оно задано и не
 * является просто номером. Фолбэк-номер — floorNumberOfLevel: как на табличках
 * в здании (−1/1/2, без нуля), а не индекс уровня в массиве.
 */
function levelLabel(v: Venue, levelId: string): string {
  const level = v.levels.find((l) => l.id === levelId)
  if (!level) return levelId
  const own = level.name ? localized(level.name, getLang(), v.venue.default_language) : ''
  const numbered = /^\s*-?\d+([.,]|\s|$)/.test(own)
  if (own && !numbered) return own
  return tr('volume3d.floor_n', { n: String(floorNumberOfLevel(v, levelId)) })
}

function unitName(v: Venue, unit: Unit): string {
  return localized(unit.name, getLang(), v.venue.default_language)
}

/** Значок «слои» на язычке этажей — язык-нейтральный, подпись под ним. */
function LayersIcon() {
  return (
    <svg class="floor-tab-ico" viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
      <path
        d="M12 3 3 8l9 5 9-5-9-5Z"
        fill="none"
        stroke="currentColor"
        stroke-width="1.8"
        stroke-linejoin="round"
      />
      <path
        d="m3 12 9 5 9-5M3 16l9 5 9-5"
        fill="none"
        stroke="currentColor"
        stroke-width="1.8"
        stroke-linejoin="round"
        stroke-linecap="round"
      />
    </svg>
  )
}

/**
 * Лифт-панель выбора рядом с язычком: «Все этажи», этажи сверху вниз со
 * счётчиками, каталог ☰. Выбор закрывает панель; тап мимо (прозрачная
 * подложка) и Esc — тоже. Повторный тап по выбранному этажу выбор не снимает:
 * для этого есть явный пункт «Все этажи».
 */
function FloorPicker(props: { v: Venue; edge: DockEdge; ratio: number; onClose: () => void }) {
  langTick.value
  const { v, edge, ratio, onClose } = props
  const active = activeLevelId.value
  const levels = [...v.levels].sort((a, b) => b.ordinal - a.ordinal)
  const place = floorPopPlacement(ratio)
  const ref = useRef<HTMLDivElement | null>(null)
  // Фокус — на выбранный пункт: с клавиатуры и читалкой выбор начинается с него.
  useEffect(() => {
    ref.current?.querySelector<HTMLButtonElement>('.mall-floor-on')?.focus()
  }, [])
  // Esc слушаем на окне, как у DockSheet: тап по заголовку панели уводит фокус
  // с пунктов, и обработчик на самой панели нажатия уже не получал.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const choose = (levelId: string | null): void => {
    activeLevelId.value = levelId
    venueMap.value?.setLevel(levelId)
    onClose()
  }

  return (
    <>
      <div class="floor-pop-backdrop" aria-hidden="true" onClick={onClose} />
      <div
        ref={ref}
        role="dialog"
        aria-label={tr('floor.title')}
        class={`floor-pop floor-pop-${edge} glass anim-fade-up`}
        style={{ top: place.top, bottom: place.bottom, maxHeight: place.maxHeight }}
      >
        <p class="tech-label floor-pop-head">{tr('floor.title')}</p>
        <button
          type="button"
          class={`mall-floor${active === null ? ' mall-floor-on' : ''}`}
          onClick={() => choose(null)}
        >
          <span class="mall-floor-name">{tr('mall.all_short')}</span>
          <span class="mall-floor-count">
            {v.levels.reduce((s, l) => s + levelTenantCount(v, l.id), 0)}
          </span>
        </button>
        {levels.map((level) => {
          // Тот же счётчик, что в этажерке и «Объёме 3D» (floor-occupants).
          const count = levelTenantCount(v, level.id)
          return (
            <button
              key={level.id}
              type="button"
              class={`mall-floor${active === level.id ? ' mall-floor-on' : ''}`}
              onClick={() => choose(level.id)}
            >
              <span class="mall-floor-name">{levelLabel(v, level.id)}</span>
              <span class="mall-floor-count">{count > 0 ? count : '—'}</span>
            </button>
          )
        })}
        <button
          type="button"
          class="mall-floor mall-dir-btn"
          onClick={() => {
            mallDirectoryOpen.value = true
            onClose()
          }}
        >
          <span aria-hidden="true">☰</span> {tr('mall.directory')}
        </button>
      </div>
    </>
  )
}

/**
 * Этажи — боковой язычок у края экрана, как SOS (просьба владельца 25.09:
 * «сбоку кнопка, на ней указан этаж или „все“; при нажатии — показ и выбор
 * этажа»). Раньше здесь стоял постоянно раскрытый рельс со всеми этажами —
 * он закрывал карту. На язычке — номер этажа, как на табличках в здании, или
 * «Все»; тап раскрывает рядом лифт-панель (FloorPicker). Перенос — тот же
 * useEdgeDock, что у SOS (порт k24, ключ dock:levels остался от рельса):
 * тянется вдоль края, за 30% ширины экрана перелетает на другой борт; тап
 * сразу после переноса панель не открывает.
 */
function FloorTab({ v }: { v: Venue }) {
  // Подписка на язык нужна КАЖДОМУ компоненту, который сам зовёт tr(): чтение
  // сигнала в родителе подписывает только родителя.
  langTick.value
  const active = activeLevelId.value
  const [open, setOpen] = useState(false)
  // Дефолт — левый край посередине дорожки: там же, где стоял рельс, и напротив
  // SOS (правый край, 0.78) — два язычка не спорят за одно место.
  const dock = useEdgeDock({ storageKey: 'dock:levels', defaultEdge: 'left', defaultRatio: 0.5 })
  const number = floorTabNumber(v, active)
  const current = number === null || active === null ? tr('mall.all_levels') : levelLabel(v, active)
  const label = tr('mall.tab_aria', { floor: current })

  return (
    <>
      <button
        type="button"
        class={`floor-tab floor-tab-${dock.edge} glass${number === null ? '' : ' floor-tab-set'}`}
        style={{ top: floorTabTop(dock.ratio), touchAction: 'none' }}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={label}
        title={`${label} · ${tr('dock.hint')}`}
        {...dock.handlers}
        onClick={() => {
          if (dock.justDragged()) return
          setOpen(!open)
        }}
      >
        <LayersIcon />
        <b class="floor-tab-value">{number ?? tr('mall.all_tab')}</b>
      </button>
      {open && (
        <FloorPicker v={v} edge={dock.edge} ratio={dock.ratio} onClose={() => setOpen(false)} />
      )}
    </>
  )
}

/** Каталог магазинов активного этажа: поиск + список, тап → карточка цели. */
function Directory({ v }: { v: Venue }) {
  langTick.value
  const [q, setQ] = useState('')
  const active = activeLevelId.value
  const units = v.units
    .filter((u) => (active ? u.level === active : true))
    .filter((u) => {
      const s = q.trim().toLowerCase()
      if (!s) return true
      return (
        unitName(v, u).toLowerCase().includes(s) ||
        (u.search_aliases ?? []).some((a) => a.toLowerCase().includes(s))
      )
    })
    .sort((a, b) => unitName(v, a).localeCompare(unitName(v, b)))

  const floorTitle = active ? levelLabel(v, active) : tr('mall.all_levels')

  return (
    <aside class="mall-dir glass anim-drawer">
      <div class="mall-dir-head">
        <div>
          <p class="tech-label mall-dir-mode">{tr('mall.mode')}</p>
          <h2 class="mall-dir-title">{floorTitle}</h2>
          <p class="tech-label mall-dir-sub">
            {tr('mall.floor_stats', { n: String(units.length) })}
          </p>
        </div>
        <button
          type="button"
          class="icon-btn"
          aria-label={tr('common.close')}
          onClick={() => {
            mallDirectoryOpen.value = false
          }}
        >
          ✕
        </button>
      </div>
      <input
        class="mall-dir-search"
        placeholder={tr('mall.search_placeholder')}
        value={q}
        onInput={(e) => setQ((e.target as HTMLInputElement).value)}
      />
      <div class="mall-dir-list">
        {units.length === 0 && <p class="mall-dir-empty">{tr('mall.empty')}</p>}
        {units.map((u) => (
          <button
            key={u.id}
            type="button"
            class={`mall-dir-row${promoTiers.value.has(u.id) ? ' promo-row' : ''}`}
            // Наведение подсвечивает объект на карте — глазами найти быстрее,
            // чем читать список из сотни строк.
            onPointerEnter={() => venueMap.value?.highlight(u.id)}
            onFocus={() => venueMap.value?.highlight(u.id)}
            onPointerLeave={() => venueMap.value?.highlight(targetId.value)}
            onClick={() => {
              mallDirectoryOpen.value = false
              // Выбрали магазин с другого этажа — переключаем и этаж, иначе он
              // остаётся невидимым под фильтром уровня.
              if (activeLevelId.value !== null && activeLevelId.value !== u.level) {
                activeLevelId.value = u.level
                venueMap.value?.setLevel(u.level)
              }
              selectTarget(u.id)
            }}
          >
            <span class="mall-dir-ico" style={{ color: catColor(u.category) }}>
              ●
            </span>
            <span class="mall-dir-name">
              <b>{unitName(v, u)}</b>
              <i class="tech-label">
                {categoryLabel(u.category)}
                {active === null ? ` · ${levelLabel(v, u.level)}` : ''}
              </i>
              {promoTiers.value.has(u.id) && <i class="promo-chip">{tr('promo.chip')}</i>}
            </span>
            <span class="mall-dir-arrow" aria-hidden="true">
              ›
            </span>
          </button>
        ))}
      </div>
    </aside>
  )
}

export function MallExplorer() {
  langTick.value
  const v = venue.value
  if (!v || v.levels.length < 2) return null
  return (
    <>
      <FloorTab v={v} />
      {mallDirectoryOpen.value && <Directory v={v} />}
    </>
  )
}
